import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getActivePractices, PracticeContextDto } from '../api/practicesAPI';
import { setActivePracticeScope } from '../api/practiceScope';
import { UserRole } from '../types/user';
import { logApiError } from '../utils/errorUtils';

import { useAuth } from './AuthContext';

/**
 * ActivePracticeContext (HA-320).
 *
 * Holds the multi-practice patient's connected practices and the currently
 * selected one. Backs the header `PracticeSwitcher` and feeds the axios
 * `?practice_id=` interceptor (via the `practiceScope` module bridge).
 *
 * Distinct from `PracticeContext` (subscription status — isActive, tier,
 * onboarding). Only Clients have multi-practice memberships in HA-320
 * scope; non-Client users skip the fetch and see no switcher.
 */

const STORAGE_KEY = 'healio:currentPracticeId';

interface ActivePracticeContextType {
  /** Practices the caller has an active relationship with. Empty until the
   *  first fetch resolves, and always empty for non-Client users. */
  activePractices: PracticeContextDto[];
  /** The selected practice id, or null before the first fetch resolves. */
  currentPracticeId: string | null;
  /** Switch the active practice. Persists to localStorage and updates the
   *  axios scope bridge. */
  setCurrentPractice: (practiceId: string) => void;
  /** True while the initial /me/practices fetch is in flight. */
  isLoading: boolean;
  /** Set when the /me/practices fetch fails; selection falls back to none. */
  error: Error | null;
}

export const ActivePracticeContext = createContext<ActivePracticeContextType | undefined>(
  undefined
);

interface ActivePracticeProviderProps {
  children: ReactNode;
}

export const ActivePracticeProvider: React.FC<ActivePracticeProviderProps> = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [activePractices, setActivePractices] = useState<PracticeContextDto[]>([]);
  const [currentPracticeId, setCurrentPracticeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const isClient = currentUser?.role === UserRole.Client;

  // Mirror the selected practice into the module-level bridge the axios
  // interceptor reads. Resolve to null when the primary practice is
  // selected — the backend falls back to the JWT primary, so sending
  // ?practice_id= would be redundant.
  const syncScope = useCallback((practiceId: string | null, practices: PracticeContextDto[]) => {
    if (!practiceId) {
      setActivePracticeScope(null);

      return;
    }
    const primary = practices.find((p) => p.isPrimary);

    setActivePracticeScope(primary?.practiceId === practiceId ? null : practiceId);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Non-Client users (and logged-out sessions) have no multi-practice
    // switcher — reset state and clear any lingering scope.
    if (!isAuthenticated || !currentUser || !isClient) {
      setActivePractices([]);
      setCurrentPracticeId(null);
      setActivePracticeScope(null);
      setIsLoading(false);
      setError(null);

      return;
    }

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const practices = await getActivePractices();

        if (cancelled) {
          return;
        }
        setActivePractices(practices);

        // Default selection: a persisted choice if it is still an active
        // practice, otherwise the primary.
        const persisted = localStorage.getItem(STORAGE_KEY);
        const persistedValid = persisted && practices.some((p) => p.practiceId === persisted);
        const primary = practices.find((p) => p.isPrimary);
        const initialId = persistedValid ? persisted : (primary?.practiceId ?? null);

        setCurrentPracticeId(initialId);
        syncScope(initialId, practices);
      } catch (err) {
        if (cancelled) {
          return;
        }
        logApiError('ActivePracticeContext.load', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, currentUser, isClient, syncScope]);

  const setCurrentPractice = useCallback(
    (practiceId: string) => {
      setCurrentPracticeId(practiceId);
      localStorage.setItem(STORAGE_KEY, practiceId);
      syncScope(practiceId, activePractices);
    },
    [activePractices, syncScope]
  );

  const contextValue = useMemo<ActivePracticeContextType>(
    () => ({
      activePractices,
      currentPracticeId,
      setCurrentPractice,
      isLoading,
      error,
    }),
    [activePractices, currentPracticeId, setCurrentPractice, isLoading, error]
  );

  return (
    <ActivePracticeContext.Provider value={contextValue}>{children}</ActivePracticeContext.Provider>
  );
};

export const useActivePractice = (): ActivePracticeContextType => {
  const context = useContext(ActivePracticeContext);

  if (context === undefined) {
    throw new Error('useActivePractice must be used within an ActivePracticeProvider');
  }

  return context;
};
